import { createAppAsyncThunk } from "appThunk";
import { DateTime } from "luxon";
import { cubeLoadParallel } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

export class CostsByStore {
    public readonly id: string;
    public readonly storeId: number;
    public readonly similarStores: SimilarStore[];
    public readonly costName: string;
    public readonly costValue: number;

    constructor(
        storeId: number,
        similarStores: SimilarStore[],
        costName: string,
        costValue: number
    ) {
        this.id = `${storeId}-${costName}`;
        this.storeId = storeId;
        this.similarStores = similarStores;
        this.costName = costName;
        this.costValue = costValue;
    }
}

class SimilarStore {
    public readonly id: number;
    public readonly similarityScore: number;

    constructor(
        rawSimilarStore: RawSimilarStore,
    ) {
        this.id = Number(rawSimilarStore.StoreId);
        this.similarityScore = Number(rawSimilarStore.Score);
    }
}

interface RawSimilarStore {
    StoreId: number,
    Score: number
}

export const loadCostsByStores = createAppAsyncThunk<
    CostsByStore[],
    { referenceDate: DateTime, useCustomCalendar: boolean, numberOfStores: number, numberOfCostTypes: number }
>(
    "customer/insights/cost/loadCostsByStores",
    async ({ referenceDate, useCustomCalendar, numberOfStores, numberOfCostTypes }, thunkAPI) => {
        try {
            const oneYearAgo = useCustomCalendar ? { weeks: 52 } : { months: 12 };
            const currentYearStartDate = referenceDate.minus(oneYearAgo).plus({ days: 1 }).startOf('day');
            const query = {
                measures: [
                    "F_Cost.SumCostValue"
                ],
                timeDimensions: [{
                    dimension: "D_Date.Date",
                    dateRange: [currentYearStartDate, referenceDate.endOf('day')]
                }],
                dimensions: [
                    "D_Store.PK_Store",
                    "D_Account.AccountName",
                    "SimilarStore.SimilarStores",
                ],
                filters: [{
                    member: "SimilarStore.SimilarStores",
                    operator: "set"
                }],
                segments: ["D_Store.OpenPhysicalStores"]
            };
            const maxRowCount = numberOfStores * numberOfCostTypes;
            const rawData = await thunkAPI.dispatch(cubeLoadParallel(query, maxRowCount));

            return rawData.map(row => {
                return new CostsByStore(
                    Number(row["D_Store.PK_Store"]),
                    (JSON.parse(row["SimilarStore.SimilarStores"] ?? "[]")
                        .map((rawSimilarStore: RawSimilarStore) =>
                            new SimilarStore(rawSimilarStore))),
                    row["D_Account.AccountName"],
                    Number(row["F_Cost.SumCostValue"])
                );
            });
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading costsByStore.", error));
            return thunkAPI.rejectWithValue(null);
        }
    }
);
